import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { organizationsApi } from 'api';
import { Helmet } from 'react-helmet';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useOrganizationPanel } from 'contexts/organizationPanelContext';
import { Program } from 'types/Program';
import { classNames } from 'primereact/utils';
import { Box, BoxSection } from 'components/_new/Box';
import { Button } from '../_new/Button';
import { NewProgramDialog } from './NewProgramDialog';
import { CopyProgramDialog } from './CopyProgramDialog';

const OrganizationProgramList = () => {
	const { t } = useTranslation();
	const {
		currentOrganization: { organizationName },
	} = useOrganizationPanel();
	const [showNewProgramDialog, setShowNewProgramDialog] = useState(false);
	const [showCopyProgramDialog, setShowCopyProgramDialog] = useState(false);
	const {
		data: ogranizationPrograms,
		isSuccess,
		refetch,
	} = useQuery(
		['getOrganizationPrograms', organizationName],
		() => organizationsApi.getOrganizationPrograms({ organizationName: String(organizationName) }),
		{
			enabled: Boolean(organizationName),
		}
	);

	const hasPrograms = isSuccess && (ogranizationPrograms || []).length > 0;

	return (
		<>
			<Helmet title={t('organizationPanel.organizationPrograms')} />

			<NewProgramDialog
				visible={showNewProgramDialog}
				onHide={() => setShowNewProgramDialog(false)}
				onSuccess={() => refetch()}
			/>

			<CopyProgramDialog
				visible={showCopyProgramDialog}
				onHide={() => setShowCopyProgramDialog(false)}
				onSuccess={() => refetch()}
			/>

			<SubPageTitle
				title={t('organizationPanel.organizationPrograms')}
				right={
					<div className="flex gap-2">
						{hasPrograms && (
							<Button
								label={t('organizationPanel.copyProgram.title')}
								variant="primary-outlined"
								onClick={() => setShowCopyProgramDialog(true)}
								icon="copy"
							/>
						)}
						<Button
							label={t('organizationPanel.addNewProgram')}
							onClick={() => setShowNewProgramDialog(true)}
							icon="plus"
						/>
					</div>
				}
			/>

			<div>
				<ul className="p-list-unstylled flex flex-column gap-2">
					{isSuccess && (ogranizationPrograms || []).length === 0 && (
						<p>{t('organizationPanel.program.list.noPrograms')}</p>
					)}
					{isSuccess &&
						(ogranizationPrograms || []).map((program: Program) => (
							<li key={program.id}>
								<Box variant="white-bordered">
									<BoxSection>
										<div className="flex flex-column gap-0 mt-2">
											<h2 className={classNames('my-0 py-1', { 'text-gray': program.finished })}>
												{program.displayName}{' '}
												{program.finished
													? `[${t('organizationPanel.programIsFinished')}]`
													: ''}
											</h2>
											{program.subtitle && (
												<p className="text-gray-600 m-0 py-1">{program.subtitle}</p>
											)}
										</div>
										<div className="flex gap-2 justify-content-end">
											{!program.finished && (
												<Link to={`/${program.name}`} className="p-button p-button-text">
													{t('organizationPanel.programHomepage')}
												</Link>
											)}
											<Link
												to={`/program/${program.name}`}
												className={classNames('p-button ', {
													'p-button-primary': !program.finished,
													'p-button-outlined': program.finished,
												})}
											>
												{t('organizationPanel.programDashboard')}
											</Link>
										</div>
									</BoxSection>
								</Box>
							</li>
						))}
				</ul>
			</div>
		</>
	);
};

export default OrganizationProgramList;
